import { Observable, of } from 'rxjs';
import { MenuItem } from '../models/menu-item';
import { MenuItemCommand } from '../types/menu-item-command';
import { MenuItemType } from '../enums/menu-item-type';
import { Guid, isNil, Nillable } from '@web/utils';
import { Icon } from '../../icon/enums/icon';
import {
  RequiredMethodCallException,
  RouteProviderService,
  RouterLink,
  RouterLinkActiveOptions,
  RouterLinkQueryParams,
} from '@web/common';

export class MenuItemBuilder {
  private _id!: Nillable<Guid>;
  private _label!: Nillable<string>;
  private _counter$!: Nillable<Observable<number>>;
  private _type!: Nillable<MenuItemType>;
  private _routerLink: Nillable<RouterLink>;
  private _icon: Nillable<Icon>;
  private _link: Nillable<string>;
  private _visibility$: Nillable<Observable<boolean>>;
  private _children: Nillable<MenuItem[]>;
  private _command: Nillable<MenuItemCommand>;
  private _queryParams: Nillable<RouterLinkQueryParams>;
  private _routerLinkActiveOptions: Nillable<RouterLinkActiveOptions>;

  public constructor(private readonly provider: RouteProviderService) {
    this.reset();
  }

  public newInstance(): MenuItemBuilder {
    return new MenuItemBuilder(this.provider);
  }

  public from(item: MenuItem): MenuItemBuilder {
    const builder = this.newInstance();

    builder._id = item.id;
    builder._label = item.label;
    builder._type = item.type;
    builder._routerLink = item.routerLink;
    builder._link = item.link;
    builder._visibility$ = item.visibility$;
    builder._icon = item.icon;
    builder._children = item.children;
    builder._command = item.command;
    builder._queryParams = item.queryParams;
    builder._routerLinkActiveOptions = item.routerLinkActiveOptions;
    builder._counter$ = item.counter$;

    return builder;
  }

  public reset(): this {
    this._id = null;
    this._label = null;
    this._type = null;
    this._icon = null;
    this._routerLink = null;
    this._link = null;
    this._children = null;
    this._command = null;
    this._queryParams = null;
    this._routerLinkActiveOptions = null;
    this._visibility$ = null;
    this._counter$ = null;

    return this;
  }

  public initRouterLink(
    routerLink: (provider: RouteProviderService) => RouterLink,
  ): this {
    return this.type(MenuItemType.RouterLink).routerLink(
      routerLink(this.provider),
    );
  }

  public initMore(children: (builder: MenuItemBuilder) => MenuItem[]): this {
    return this.type(MenuItemType.More).children((builder) =>
      children(builder),
    );
  }

  public initLink(link: string): this {
    return this.type(MenuItemType.Link).link(link);
  }

  public initCommand(command: MenuItemCommand): this {
    return this.type(MenuItemType.Command).command(command);
  }

  public id(id: Guid): this {
    this._id = id;

    return this;
  }

  public routerLinkQueryParams(params: RouterLinkQueryParams): this {
    this._queryParams = params;

    return this;
  }

  public routerLinkActiveOptions(
    routerLinkActiveOptions: RouterLinkActiveOptions,
  ): this {
    this._routerLinkActiveOptions = routerLinkActiveOptions;

    return this;
  }

  public routerLinkActiveSkipExact(): this {
    return this.routerLinkActiveOptions({ exact: false });
  }

  public label(label: string): this {
    this._label = label;

    return this;
  }

  public counter(counter$: Observable<number>): this {
    this._counter$ = counter$;

    return this;
  }

  public type(type: MenuItemType): this {
    this._type = type;

    return this;
  }

  public routerLink(routerLink: RouterLink): this {
    this._routerLink = routerLink;

    return this;
  }

  public link(link: string): this {
    this._link = link;

    return this;
  }

  public icon(icon: Icon): this {
    this._icon = icon;

    return this;
  }

  public children(factory: (builder: MenuItemBuilder) => MenuItem[]): this {
    this._children = factory(this.newInstance());

    return this;
  }

  public visibility(visibility$: Observable<boolean>): this {
    this._visibility$ = visibility$;

    return this;
  }

  public command(command: MenuItemCommand): this {
    this._command = command;

    return this;
  }

  public build(): MenuItem {
    this.requireId();
    this.requireLabel();
    this.requireType();
    this.configureDefaultVisibility();
    this.configureDefaultRouterLinkActiveOptions();

    if (this.isRouterLink()) {
      this.requireRouterLink();
    }

    if (this.isLink()) {
      this.requireLink();
    }

    if (this.isCommand()) {
      this.requireCommand();
    }

    if (this.isMore()) {
      this.requireChildren();
    }

    const item = new MenuItem({
      id: this._id!,
      label: this._label!,
      type: this._type!,
      visibility$: this._visibility$!,
      routerLinkActiveOptions: this._routerLinkActiveOptions!,
      icon: this._icon,
      routerLink: this._routerLink,
      link: this._link,
      children: this._children,
      command: this._command,
      queryParams: this._queryParams,
      counter$: this._counter$,
    });

    this.reset();

    return item;
  }

  private requireId(): void {
    if (isNil(this._id)) {
      throw new RequiredMethodCallException(this.id.name);
    }
  }

  private requireLabel(): void {
    if (isNil(this._label)) {
      throw new RequiredMethodCallException(this.label.name);
    }
  }

  private requireType(): void {
    if (isNil(this._type)) {
      throw new RequiredMethodCallException(this.type.name);
    }
  }

  private requireRouterLink(): void {
    if (isNil(this._routerLink)) {
      throw new RequiredMethodCallException(this.routerLink.name);
    }
  }

  private requireLink(): void {
    if (isNil(this._link)) {
      throw new RequiredMethodCallException(this.link.name);
    }
  }

  private requireCommand(): void {
    if (isNil(this._command)) {
      throw new RequiredMethodCallException(this.command.name);
    }
  }

  private requireChildren(): void {
    if (isNil(this._children)) {
      throw new RequiredMethodCallException(this.children.name);
    }
  }

  private isRouterLink(): boolean {
    return this._type === MenuItemType.RouterLink;
  }

  private isLink(): boolean {
    return this._type === MenuItemType.Link;
  }

  private isCommand(): boolean {
    return this._type === MenuItemType.Command;
  }

  private isMore(): boolean {
    return this._type === MenuItemType.More;
  }

  private configureDefaultVisibility(): void {
    if (!isNil(this._visibility$)) {
      return;
    }

    this.visibility(of(true));
  }

  private configureDefaultRouterLinkActiveOptions(): void {
    if (!isNil(this._routerLinkActiveOptions)) {
      return;
    }

    this.routerLinkActiveOptions({
      exact: true,
    });
  }
}
